import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FlightSearchWidget from "@/components/FlightSearchWidget";
import { api, AIRLINE_LOGOS, formatDuration, formatPrice } from "@/lib/api";
import { DESTINATIONS } from "@/data/content";
import { getDestinationContent, getRelatedDestinations } from "@/data/routes";
import { BLOG_POSTS } from "@/data/blogs";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/PhoneCTA";
import { useSEO, breadcrumbsSchema, SITE_URL } from "@/lib/seo";
import {
  AirplaneInFlight, ArrowRight, CalendarBlank, ClockClockwise, Headset,
  MapPin, Phone, Sparkle,
} from "@phosphor-icons/react";
import { Skeleton } from "@/components/ui/skeleton";

const DEFAULT_ORIGIN = "JFK";
const DEFAULT_CABIN = "economy";

/**
 * Resolve a destination by IATA code from either:
 *   1. The hand-curated DESTINATIONS in content.js (4 featured home-page tiles,
 *      with custom imagery and fuller editorial).
 *   2. The 23 routes.js destinations (auto-composed via getDestinationContent).
 *
 * Returns null for unknown codes so the page can 404 cleanly.
 */
function findDestination(code) {
  if (!code) return null;
  const upper = code.toUpperCase();
  const featured = DESTINATIONS.find((d) => d.code === upper);
  if (featured) return featured;
  return getDestinationContent(upper, { blogPosts: BLOG_POSTS });
}

function FlightCard({ flight, onSelect }) {
  return (
    <div className="bg-white border border-[#E0DDD6] p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center lift" data-testid={`flight-card-${flight.id}`}>
      <div className="md:col-span-2 flex items-center gap-3">
        <div className="w-12 h-12 bg-[#F7F5F0] flex items-center justify-center border border-[#E0DDD6]">
          <img src={AIRLINE_LOGOS[flight.airline_code]} alt={flight.airline_name} className="w-10 h-10 object-contain" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#1C1C1C]">{flight.airline_name}</p>
          <p className="text-xs text-[#666460]">{flight.flight_number}</p>
        </div>
      </div>
      <div className="md:col-span-6 flex items-center justify-between">
        <div className="text-left">
          <p className="font-serif text-3xl text-[#1C1C1C] tracking-tight">{flight.depart_time}</p>
          <p className="text-xs text-[#666460] mt-1">{flight.origin}</p>
        </div>
        <div className="flex-1 px-4">
          <p className="text-xs text-[#666460] text-center mb-1">{formatDuration(flight.duration_minutes)}</p>
          <div className="relative h-px bg-[#E0DDD6]">
            <AirplaneInFlight size={16} className="absolute left-1/2 -translate-x-1/2 -top-2 text-[#C29B62]" weight="duotone" />
          </div>
          <p className="text-xs text-[#666460] text-center mt-1">{flight.stops === 0 ? "Non-stop" : `${flight.stops} stop`}</p>
        </div>
        <div className="text-right">
          <p className="font-serif text-3xl text-[#1C1C1C] tracking-tight">{flight.arrive_time}</p>
          <p className="text-xs text-[#666460] mt-1">{flight.destination}</p>
        </div>
      </div>
      <div className="md:col-span-4 flex md:flex-col items-center md:items-end justify-between gap-3 md:gap-2 md:border-l md:border-[#E0DDD6] md:pl-6">
        <div className="text-left md:text-right">
          <p className="text-xs text-[#666460]">{flight.fare_class} · {flight.cabin}</p>
          <p className="font-serif text-3xl" style={{ color: "#C29B62" }}>{formatPrice(flight.price)}</p>
        </div>
        <button
          onClick={() => onSelect(flight)}
          className="btn-gold text-xs tracking-[0.16em] uppercase"
          data-testid={`select-${flight.id}`}
        >
          Select <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function Fact({ icon: I, label, value }) {
  return (
    <div className="bg-white border border-[#E0DDD6] p-5">
      <I size={22} className="text-[#C29B62]" weight="duotone" />
      <p className="tcp-overline text-[#666460] mt-3">{label}</p>
      <p className="font-serif text-xl text-[#1C1C1C] tracking-tight mt-1">{value}</p>
    </div>
  );
}

export default function DestinationPage() {
  const { code } = useParams();
  const nav = useNavigate();
  // Memoise so we get a stable reference per `code`. Without this,
  // getDestinationContent() returns a fresh object every render which
  // would cascade into re-running the search effect indefinitely.
  const dest = useMemo(() => findDestination(code), [code]);

  // Selected cabin is reactive — drives both the inline results query AND
  // the FlightSearchWidget pre-fill below.
  const [cabin, setCabin] = useState(DEFAULT_CABIN);

  const query = useMemo(() => ({
    trip_type: "round_trip",
    origin: DEFAULT_ORIGIN,
    destination: dest ? dest.code : "",
    depart_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    return_date: new Date(Date.now() + 37 * 86400000).toISOString().slice(0, 10),
    passengers: 1,
    cabin,
  }), [dest, cabin]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Edge-cached fare snapshot — loads near-instantly thanks to server-side
  // stale-while-revalidate caching (TTL 1h fresh / 24h stale).
  const [snapshot, setSnapshot] = useState(null);

  useSEO(
    dest
      ? {
          title: `Flights to ${dest.city}, ${dest.country} | TheCityPair`,
          description: dest.description,
          canonical: `/destinations/${dest.code}`,
          og: { image: dest.img, type: "article" },
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "TouristDestination",
              "@id": `${SITE_URL}/destinations/${dest.code}`,
              name: `${dest.city}, ${dest.country}`,
              description: dest.description,
              image: dest.img,
              touristType: dest.best_for,
              geo: { "@type": "Place", name: dest.region },
            },
            breadcrumbsSchema([
              { name: "Home", url: "/" },
              { name: "Destinations", url: "/" },
              { name: dest.city, url: `/destinations/${dest.code}` },
            ]),
          ],
        }
      : { title: "Destination not found | TheCityPair", noindex: true }
  );

  useEffect(() => { window.scrollTo(0, 0); setCabin(DEFAULT_CABIN); }, [code]);

  // 1. Snapshot endpoint — instant (cached), so the "From $X" badge renders
  //    before the heavy /search call below completes.
  useEffect(() => {
    if (!dest) return;
    setSnapshot(null);
    api.get(`/products/flights/fare-snapshot?origin=${DEFAULT_ORIGIN}&destination=${dest.code}`)
      .then((r) => setSnapshot(r.data))
      .catch(() => {});
  }, [dest]);

  useEffect(() => {
    if (!dest) return;
    setLoading(true); setError(null);
    api.post("/products/flights/search", query)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [dest, query]);

  if (!dest) return <Navigate to="/" replace />;

  const onSelect = (flight) => {
    sessionStorage.setItem("tcp_selected_flight", JSON.stringify({
      ...flight,
      passengers: query.passengers,
      return_date: query.return_date,
    }));
    nav("/booking");
  };

  const outbound = data?.outbound || [];
  // Sibling destinations in the same regional grouping (Asia, Europe, etc.).
  // Excluded for featured destinations not in any blog grouping (e.g. Tashkent).
  const related = getRelatedDestinations(dest.code, { blogPosts: BLOG_POSTS });

  return (
    <div className="bg-[#F7F5F0] min-h-screen" data-testid="destination-page">
      {/* Editorial hero */}
      <div className="bg-[#0A0A0B] relative">
        <Navbar dark />
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-0">
          <div className="lg:col-span-7 px-6 md:px-12 lg:px-16 pt-32 pb-16 lg:pb-24">
            <p className="tcp-overline text-[#C29B62] mb-4">{dest.country} · {dest.region}</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#F7F5F0]" data-testid="destination-title">
              {dest.city}.
            </h1>
            <p className="font-serif italic text-xl md:text-2xl text-[#C29B62] mt-4">{dest.tagline}</p>
            {snapshot?.price && (
              <div className="mt-6 inline-flex items-baseline gap-3 border border-[#C29B62]/40 px-5 py-3" data-testid="destination-snapshot-price">
                <span className="text-[10px] tracking-[0.18em] uppercase text-[#A09E9A]">From</span>
                <span className="font-serif text-3xl md:text-4xl text-[#C29B62] tracking-tighter">
                  {new Intl.NumberFormat("en-US", { style: "currency", currency: snapshot.currency || "USD", maximumFractionDigits: 0 }).format(snapshot.price)}
                </span>
                <span className="text-xs text-[#A09E9A]">one-way · {snapshot.airline_name || "live fare"}</span>
              </div>
            )}
            <p className="text-[#A09E9A] mt-8 leading-relaxed max-w-xl text-[15px]">
              {dest.description}
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {dest.best_for.map((tag) => (
                <span
                  key={tag}
                  data-testid={`destination-tag-${tag.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs tracking-[0.16em] uppercase border border-white/15 text-[#A09E9A]"
                >
                  <Sparkle size={12} weight="fill" className="text-[#C29B62]" /> {tag}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative min-h-[300px] lg:min-h-[520px]">
            <img src={dest.img} alt={dest.city} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0A0A0B]/30" />
          </div>
        </div>
      </div>

      {/* At-a-glance facts */}
      <section className="px-6 md:px-12 lg:px-24 pt-16 md:pt-20 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Fact icon={MapPin} label="Region" value={dest.region} />
          <Fact icon={ClockClockwise} label="Flight time from JFK" value={`~${dest.typical_flight_hours}h`} />
          <Fact icon={CalendarBlank} label="Best season" value={dest.season} />
          <Fact icon={AirplaneInFlight} label="Airport" value={`${dest.city.split(" ")[0]} · ${dest.code}`} />
        </div>
      </section>

      {/* Search widget — pre-populated, dark themed */}
      <section className="px-6 md:px-12 lg:px-24 py-10">
        <div className="bg-[#0A0A0B] p-6 md:p-8">
          <p className="tcp-overline text-[#C29B62] mb-3">Search Flights</p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tighter text-[#F7F5F0] mb-6">
            Tailor your trip to <em className="italic">{dest.city}.</em>
          </h2>
          <FlightSearchWidget
            key={cabin}
            initialOrigin={DEFAULT_ORIGIN}
            initialDestination={dest.code}
            initialCabin={cabin}
            initialTripType={query.trip_type}
          />
        </div>
      </section>

      {/* Live results inline */}
      <section className="px-6 md:px-12 lg:px-24 pb-20" data-testid="destination-results">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <p className="tcp-overline text-[#C29B62] mb-2">Live Fares</p>
            <h3 className="font-serif text-2xl md:text-3xl text-[#1C1C1C] tracking-tight">
              JFK → {dest.code} · {query.cabin.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())} · {query.depart_date}
            </h3>
          </div>
          {!loading && outbound.length > 0 && (
            <Link
              to={`/search?trip_type=${query.trip_type}&origin=${query.origin}&destination=${query.destination}&depart_date=${query.depart_date}&return_date=${query.return_date}&passengers=${query.passengers}&cabin=${query.cabin}`}
              data-testid="destination-see-all"
              className="text-xs tracking-[0.18em] uppercase text-[#C29B62] hover:text-[#A88550] border-b border-[#C29B62] hover:border-[#A88550] pb-1 inline-flex items-center gap-2 whitespace-nowrap"
            >
              See all & change dates <ArrowRight size={14} />
            </Link>
          )}
        </div>

        {/* Cabin tabs — flip the widget + reissue the inline search without leaving the page. */}
        <div className="mb-6 flex flex-wrap gap-2" data-testid="destination-cabin-tabs">
          {[
            { value: "economy",         label: "Economy" },
            { value: "premium_economy", label: "Premium" },
            { value: "business",        label: "Business" },
            { value: "first",           label: "First" },
          ].map((c) => {
            const active = cabin === c.value;
            return (
              <button
                key={c.value}
                onClick={() => setCabin(c.value)}
                aria-pressed={active}
                data-testid={`destination-cabin-tab-${c.value}`}
                className={
                  "inline-flex items-center px-4 py-2 text-xs tracking-[0.16em] uppercase border transition-colors " +
                  (active
                    ? "bg-[#1C1C1C] text-[#F7F5F0] border-[#1C1C1C]"
                    : "bg-white text-[#1C1C1C] border-[#E0DDD6] hover:border-[#C29B62] hover:text-[#C29B62]")
                }
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="space-y-4" data-testid="destination-loading">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 w-full" />)}
          </div>
        )}

        {error && (
          <div className="p-6 bg-white border border-red-200 text-red-700" data-testid="destination-error">
            Could not load fares: {error}
          </div>
        )}

        {!loading && !error && outbound.length === 0 && (
          <div className="bg-[#0A0A0B] text-[#F7F5F0] p-8 md:p-10" data-testid="destination-no-results">
            <div className="flex items-start gap-4 mb-5">
              <Headset size={32} weight="duotone" className="text-[#C29B62] shrink-0" />
              <div>
                <p className="tcp-overline text-[#C29B62] mb-2">Nothing online for this route</p>
                <h4 className="font-serif text-2xl md:text-3xl tracking-tighter leading-tight">
                  Premium fares to {dest.city} often live <em className="italic">off-channel.</em>
                </h4>
              </div>
            </div>
            <p className="text-[#A09E9A] mb-6 max-w-2xl leading-relaxed">
              Our travel desk has access to consolidator and airline-direct contracts that don't appear
              in metasearch — especially for business-class routings into {dest.city}. Tell us your
              dates and we'll put together a quote in under 24 hours.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href={`tel:${PHONE_TEL}`} data-testid="destination-call-btn" className="btn-gold inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase">
                <Phone size={14} weight="fill" /> Call {PHONE_DISPLAY}
              </a>
              <Link to="/contact" data-testid="destination-contact-btn" className="btn-outline-light text-xs tracking-[0.16em] uppercase">
                Request a Call-Back
              </Link>
            </div>
          </div>
        )}

        {!loading && !error && outbound.length > 0 && (
          <div className="space-y-4">
            {outbound.slice(0, 5).map((f) => <FlightCard key={f.id} flight={f} onSelect={onSelect} />)}
          </div>
        )}
      </section>

      {/* Related destinations — keep visitors inside the funnel */}
      {related.length > 0 && (
        <section className="bg-[#0A0A0B] text-[#F7F5F0] px-6 md:px-12 lg:px-24 py-20 md:py-24" data-testid="destination-related">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-xl">
              <p className="tcp-overline text-[#C29B62] mb-3">Discover More</p>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tighter leading-tight">
                Pair it with another <em className="italic text-[#C29B62]">stop on the same trip.</em>
              </h2>
              <p className="text-[#A09E9A] mt-4 text-[15px] leading-relaxed">
                Editor-picked destinations from the same region as {dest.city} — our desk routinely
                builds multi-city itineraries with the right cabin on every leg.
              </p>
            </div>
            <Link
              to="/journal"
              data-testid="destination-related-journal-link"
              className="self-start md:self-end inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#C29B62] hover:text-[#F7F5F0] border-b border-[#C29B62] hover:border-[#F7F5F0] pb-1 transition-colors whitespace-nowrap"
            >
              All travel stories <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {related.map((r) => (
              <Link
                key={r.code}
                to={`/destinations/${r.code}`}
                data-testid={`destination-related-${r.code}`}
                className="group relative overflow-hidden aspect-[4/5] lift"
              >
                {r.img && (
                  <img
                    src={r.img}
                    alt={r.city}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
                <div className="absolute inset-0 p-5 md:p-6 flex flex-col justify-end">
                  <p className="tcp-overline text-[#C29B62] mb-2">{r.country}</p>
                  <div className="flex items-end justify-between gap-2">
                    <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight">{r.city}</h3>
                    <ArrowRight size={20} className="text-[#C29B62] shrink-0 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                  </div>
                  <p className="font-serif italic text-sm text-[#A09E9A] mt-2 line-clamp-2">{r.tagline}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
